import { useState } from 'react';
import type { TicketTypeGroup } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { TicketTypeGroupFormModal } from './TicketTypeGroupFormModal.js';
import { useTicketTypeGroups } from './api.js';

export function TicketTypeGroupsPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TicketTypeGroup | null>(null);

  const query = useTicketTypeGroups({ page, pageSize: DEFAULT_PAGE_SIZE, sortBy: 'sortOrder' });
  const groups = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (group: TicketTypeGroup): void => {
    setEditing(group);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Ticket Type Groups"
        description="Organize ticket types into groups (e.g. Support, Onboarding)."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="ticketTypes" className="h-4 w-4" />
              New group
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Order</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={groups.length === 0}
              emptyTitle="No ticket type groups found"
              emptyDescription="Create your first group to get started."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New group</Button> : undefined}
            >
              {groups.map((group) => (
                <tr
                  key={group.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(group)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">{group.sortOrder}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{group.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={group.isActive ? 'green' : 'gray'}>
                      {group.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && groups.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <TicketTypeGroupFormModal
        isOpen={formOpen}
        group={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
